import { type FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/admin';

// テナント単位のシステム設定(マスタメニュー)。会社で1個・全員共通(ユーザーごとには分けない)。
// 現状は「チャットの応答待ちタイムアウト(分)」のみ。今後の全社設定もここに追加していく。
const SETTINGS_KEY = ['tenant-settings'];

export function SystemSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => adminApi.getTenantSettings(),
  });

  // 入力中の値(分)。サーバ値が来たら同期する。
  const [minutes, setMinutes] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setMinutes(String(data.responseTimeoutMinutes));
  }, [data]);

  const save = useMutation({
    mutationFn: (responseTimeoutMinutes: number) =>
      adminApi.updateTenantSettings({ responseTimeoutMinutes }),
    onSuccess: (res) => {
      qc.setQueryData(SETTINGS_KEY, res);
      setSaved(true);
      setErr(null);
      window.setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : '保存に失敗しました'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    const n = Number(minutes);
    if (!Number.isInteger(n) || n < 1 || n > 1440) {
      setErr('1〜1440(分)の整数で入力してください。');
      return;
    }
    save.mutate(n);
  };

  const dirty = data ? String(data.responseTimeoutMinutes) !== minutes : false;

  return (
    <div style={{ maxWidth: 640 }}>
      <h3 style={{ margin: '0 0 4px' }}>システム設定</h3>
      <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: 13 }}>
        会社全体に効く設定です（ユーザーごとには分かれません）。
      </p>

      {isLoading ? (
        <p style={{ color: '#64748b' }}>読み込み中…</p>
      ) : (
        <form onSubmit={submit} style={card}>
          <strong style={{ fontSize: 14 }}>チャットの応答待ちタイムアウト</strong>
          <p style={{ margin: '2px 0 10px', color: '#64748b', fontSize: 12.5, lineHeight: 1.6 }}>
            チャットからデバイスに指示したあと、結果を待つ最大時間（分）。これを超えると
            「タイムアウト」として打ち切ります。claude の実行が長い場合に備えた上限値なので、
            長めに設定して構いません（既定 30 分）。
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="number"
              min={1}
              max={1440}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              style={{ width: 110 }}
            />
            <span style={{ color: '#475569', fontSize: 13 }}>分</span>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>（1〜1440 / 24時間まで）</span>
          </div>

          {err && <p style={{ color: '#dc2626', margin: '10px 0 0', fontSize: 13 }}>{err}</p>}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
            <button type="submit" disabled={save.isPending || !dirty}>
              {save.isPending ? '保存中…' : '保存'}
            </button>
            {saved && <span style={{ color: '#166534', fontSize: 13 }}>✓ 保存しました</span>}
          </div>
        </form>
      )}
    </div>
  );
}

const card: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
  border: '1px solid #e7ebf0',
  borderRadius: 12,
  padding: '18px 20px',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
};
